import { ReactNode } from 'react'
import { RefreshCw } from 'lucide-react'
import { usePullToRefresh } from '@/hooks/usePullToRefresh'
import { cn } from '@/lib/utils'

interface PullToRefreshProps {
  children: ReactNode
  onRefresh: () => Promise<unknown>
  threshold?: number
  className?: string
}

export function PullToRefresh({
  children,
  onRefresh,
  threshold = 80,
  className,
}: PullToRefreshProps) {
  const { containerRef, pullDistance, isRefreshing, isPulling } =
    usePullToRefresh({
      onRefresh,
      threshold,
    })

  const progress = Math.min(pullDistance / threshold, 1)
  const shouldTrigger = pullDistance >= threshold

  return (
    <div
      ref={containerRef}
      className={cn('relative overflow-auto', className)}
    >
      {/* Pull indicator */}
      <div
        className="absolute left-0 right-0 flex justify-center overflow-hidden pointer-events-none z-10"
        style={{
          height: pullDistance,
          top: 0,
          transition: isPulling ? 'none' : 'height 0.2s ease-out',
        }}
      >
        <div
          className={cn(
            'flex items-center justify-center rounded-full bg-muted',
            'w-10 h-10 mt-2 transition-all duration-200',
            shouldTrigger && 'bg-primary/10'
          )}
          style={{
            opacity: Math.min(progress * 1.5, 1),
            transform: `scale(${0.5 + progress * 0.5})`,
          }}
        >
          <RefreshCw
            className={cn(
              'h-5 w-5 text-muted-foreground transition-colors',
              shouldTrigger && 'text-primary',
              isRefreshing && 'animate-spin'
            )}
            style={{
              transform: isRefreshing
                ? 'none'
                : `rotate(${progress * 180}deg)`,
            }}
          />
        </div>
      </div>

      {/* Content with pull offset */}
      <div
        style={{
          transform: `translateY(${pullDistance}px)`,
          transition: isPulling ? 'none' : 'transform 0.2s ease-out',
        }}
      >
        {children}
      </div>
    </div>
  )
}
